// services/userService.js
import { apiClient, API_ENDPOINTS } from './api';

const userService = {
  /**
   * Получить список всех пользователей
   * @returns {Promise<Array>} Массив пользователей
   */
  async getAll() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.USERS);
      
      // Нормализация ответа
      let users = [];
      if (Array.isArray(response)) {
        users = response;
      } else if (response?.data && Array.isArray(response.data)) {
        users = response.data;
      } else if (response?.users?.data) {
        users = response.users.data;
      } else if (response?.users && Array.isArray(response.users)) {
        users = response.users;
      }
      
      return users.map(user => this.normalizeUser(user));
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
      throw error;
    }
  },

  /**
   * Получить пользователя по ID
   * @param {number} id - ID пользователя
   * @returns {Promise<Object>} Данные пользователя
   */
  async getById(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.USER(id));
      return this.normalizeUser(response?.data || response);
    } catch (error) {
      console.error(`Ошибка загрузки пользователя ${id}:`, error);
      throw error;
    }
  },

  /**
   * Создать нового пользователя
   * @param {Object} userData - Данные пользователя
   * @param {number} userData.employee_id - ID сотрудника
   * @param {string} userData.username - Логин
   * @param {string} [userData.role='user'] - Роль
   * @param {string} [userData.status='active'] - Статус
   * @returns {Promise<Object>} Созданный пользователь с временным паролем
   */
  async create(userData) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.USERS, {
        employee_id: userData.employee_id,
        username: userData.username,
        role: userData.role || 'user',
        status: userData.status || 'active'
      });
      
      return {
        success: response.success,
        message: response.message,
        user: response.data ? this.normalizeUser(response.data) : null,
        temporaryPassword: response.data?.temporaryPassword
      };
    } catch (error) {
      console.error('Ошибка создания пользователя:', error);
      throw error;
    }
  },

  /**
   * Обновить данные пользователя
   * @param {number} id - ID пользователя
   * @param {Object} userData - Обновляемые данные
   * @returns {Promise<Object>} Обновленный пользователь
   */
  async update(id, userData) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.USER(id), userData);
      return this.normalizeUser(response?.data || response);
    } catch (error) {
      console.error(`Ошибка обновления пользователя ${id}:`, error);
      throw error;
    }
  },

  /**
   * Удалить пользователя
   * @param {number} id - ID пользователя
   * @returns {Promise<Object>} Результат удаления
   */
  async delete(id) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.USER(id));
      return { success: true, message: response.message || 'Пользователь удален' };
    } catch (error) {
      console.error(`Ошибка удаления пользователя ${id}:`, error);
      throw error;
    }
  },

  /**
   * Сбросить пароль пользователя (для админа)
   * @param {number} id - ID пользователя
   * @returns {Promise<Object>} Новый временный пароль
   */
  async resetPassword(id) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.USER_PASSWORD_RESET(id));
      return {
        success: response.success,
        message: response.message,
        temporaryPassword: response.temporaryPassword || response.data?.temporaryPassword
      };
    } catch (error) {
      console.error(`Ошибка сброса пароля пользователя ${id}:`, error);
      throw error;
    }
  },

  /**
   * Сменить пароль текущего пользователя
   * @param {Object} passwordData - Данные для смены пароля
   * @param {string} passwordData.current_password - Текущий пароль
   * @param {string} passwordData.new_password - Новый пароль
   * @param {string} passwordData.new_password_confirmation - Подтверждение нового пароля
   * @returns {Promise<Object>} Результат смены пароля
   */
  async changePassword(passwordData) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.USER_PASSWORD, passwordData);
      return {
        success: response.success,
        message: response.message || 'Пароль успешно изменен'
      };
    } catch (error) {
      console.error('Ошибка смены пароля:', error);
      throw error;
    }
  },

  /**
   * Получить список ролей
   * @returns {Promise<Array>} Массив ролей
   */
  async getRoles() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.USERS_ROLES);
      
      let roles = [];
      if (Array.isArray(response)) {
        roles = response;
      } else if (response?.roles && Array.isArray(response.roles)) {
        roles = response.roles;
      } else if (response?.data && Array.isArray(response.data)) {
        roles = response.data;
      }
      
      return roles;
    } catch (error) {
      console.error('Ошибка загрузки ролей:', error);
      // Возвращаем стандартные роли в случае ошибки
      return [
        { value: 'user', label: 'Пользователь' },
        { value: 'training_curator', label: 'Куратор обучения' },
        { value: 'hr_manager', label: 'HR-менеджер' },
        { value: 'admin', label: 'Администратор' }
      ];
    }
  },

  /**
   * Получить сотрудников без учетной записи
   * @returns {Promise<Object>} Список сотрудников и их количество
   */
  async getEmployeesWithoutAccount() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.EMPLOYEES_WITHOUT_ACCOUNT);
      
      let employees = [];
      if (response?.employees && Array.isArray(response.employees)) {
        employees = response.employees;
      } else if (Array.isArray(response)) {
        employees = response;
      }
      
      return {
        employees: employees,
        total: response?.total || employees.length
      };
    } catch (error) {
      console.error('Ошибка загрузки сотрудников без аккаунта:', error);
      throw error;
    }
  },

  /**
   * Изменить статус пользователя (активен/заблокирован)
   * @param {number} id - ID пользователя
   * @param {string} status - Новый статус ('active' или 'inactive')
   * @returns {Promise<Object>} Обновленный пользователь
   */
  async changeStatus(id, status) {
    return this.update(id, { status });
  },

  /**
   * Нормализация данных пользователя
   * @param {Object} user - Данные пользователя из API
   * @returns {Object} Нормализованные данные
   */
  normalizeUser(user) {
    if (!user) return null;
    
    return {
      id: user.id,
      username: user.username || '',
      fullName: user.fullName || user.name || '',
      email: user.email || '',
      role: user.role || 'user',
      status: user.status || 'active',
      avatar: user.avatar || null,
      employee_id: user.employee_id || null,
      lastLogin: user.lastLogin || user.last_login || null,
      createdAt: user.createdAt || user.created_at || null,
      updatedAt: user.updatedAt || user.updated_at || null
    };
  }
};

export default userService;